import { useMemo, useState } from "react";
import { Swords, Trophy } from "lucide-react";

import { Avatar, Badge, Button, Sheet, getInitials, tintForId } from "@/ui";
import { useReportMatch, type ActiveMatch, type BracketTeam } from "@/features/queries";
import { noAutofill } from "@/lib/noAutofill";

interface Props {
  active: ActiveMatch;
}

/** Main-screen card shown above "Ближайшие события" when the user has a
 *  READY or WAITING_CONFIRM tournament match. Tapping it opens the
 *  report sheet so players can amend their vote until both sides agree
 *  (or an admin writes the result). */
export function ActiveMatchCard({ active }: Props) {
  const [reporting, setReporting] = useState(false);
  const myTeam = teamById(active, active.my_team_id);
  const oppTeam =
    active.opponent_team_id != null
      ? teamById(active, active.opponent_team_id)
      : null;
  const status = activeStatus(active);

  return (
    <>
      <article
        onClick={() => setReporting(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setReporting(true);
          }
        }}
        style={{
          margin: "0 16px 12px",
          padding: 16,
          borderRadius: "var(--r-lg)",
          background:
            "linear-gradient(135deg, rgba(255,90,31,0.12), rgba(255,184,0,0.08))",
          border: "1px solid rgba(255,90,31,0.35)",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Badge kind="brand">Твой матч</Badge>
          <Badge kind="neutral">До {halfGamesToWin(active.best_of)} побед</Badge>
          <span
            style={{
              marginLeft: "auto",
              fontSize: 13,
              color: "var(--fg2)",
              fontWeight: 600,
            }}
          >
            {active.tournament_name}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <TeamBlock team={myTeam} align="left" />
          <Swords size={18} color="var(--brand-orange)" />
          <TeamBlock team={oppTeam} align="right" />
        </div>

        <div
          className="caption"
          style={{
            color: status.tone === "danger"
              ? "var(--fg-danger)"
              : status.tone === "muted"
                ? "var(--fg2)"
                : "var(--brand-orange)",
            fontWeight: 600,
          }}
        >
          {status.text}
        </div>
      </article>

      <ReportMatchSheet
        active={active}
        open={reporting}
        onClose={() => setReporting(false)}
      />
    </>
  );
}

type StatusTone = "brand" | "muted" | "danger";
function activeStatus(active: ActiveMatch): { text: string; tone: StatusTone } {
  // Both sides reported but pointed at different winners → mismatch.
  // Card stays visible (state = WAITING_CONFIRM) so anyone can amend.
  const conflict =
    active.reported_by_me &&
    active.opponent_reported &&
    active.reported_winner_team_id !== active.opponent_reported_winner_team_id;
  if (conflict) {
    return {
      text: "Результаты не совпали — поправьте счёт, чтобы согласовать.",
      tone: "danger",
    };
  }
  if (active.reported_by_me) {
    return { text: "Ты уже сообщил результат — ждём соперника.", tone: "muted" };
  }
  if (active.opponent_reported) {
    return { text: "Соперник прислал счёт — подтверди или поправь.", tone: "brand" };
  }
  return { text: "Нажми, чтобы ввести счёт", tone: "brand" };
}

function teamById(active: ActiveMatch, id: number): BracketTeam | null {
  if (active.match.team_a?.id === id) return active.match.team_a;
  if (active.match.team_b?.id === id) return active.match.team_b;
  return null;
}

function halfGamesToWin(bestOf: number): number {
  return Math.floor(bestOf / 2) + 1;
}

function teamDisplayName(team: BracketTeam | null): string {
  if (!team) return "—";
  if (team.is_bye) return "BYE";
  if (team.members.length === 0) return team.name ?? "команда";
  return team.members
    .map((m) => {
      const n = [m.first_name, m.last_name].filter(Boolean).join(" ");
      return n || m.username || `player-${m.user_id}`;
    })
    .join(", ");
}

function TeamBlock({
  team,
  align,
}: {
  team: BracketTeam | null;
  align: "left" | "right";
}) {
  const first = team?.members[0];
  const label = teamDisplayName(team);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flex: 1,
        minWidth: 0,
        flexDirection: align === "right" ? "row-reverse" : "row",
      }}
    >
      {first ? (
        <Avatar
          initials={getInitials(first.first_name, first.last_name, first.username)}
          size={36}
          tint={tintForId(first.user_id)}
          src={first.photo_url}
        />
      ) : (
        <div style={{ width: 36, height: 36 }} />
      )}
      <div style={{ flex: 1, minWidth: 0, textAlign: align === "right" ? "right" : "left" }}>
        <div
          className="body"
          style={{
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </div>
      </div>
    </div>
  );
}

// ---------- Report sheet ---------------------------------------------------

function ReportMatchSheet({
  active,
  open,
  onClose,
}: {
  active: ActiveMatch;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Sheet open={open} onClose={onClose} title="Результат матча">
      {open && <ReportForm active={active} onClose={onClose} />}
    </Sheet>
  );
}

interface GameRow {
  a: string; // my team score (string so the input can be cleared)
  b: string; // opponent score
}

function ReportForm({ active, onClose }: { active: ActiveMatch; onClose: () => void }) {
  const report = useReportMatch(active.tournament_id);
  const target = halfGamesToWin(active.best_of); // games needed to win

  const [rows, setRows] = useState<GameRow[]>(() => {
    const existing = active.match.games ?? [];
    if (existing.length > 0) {
      return existing.map((g) => ({ a: String(g.a), b: String(g.b) }));
    }
    return [{ a: "", b: "" }];
  });

  const { myWins, oppWins, valid, winnerTeamId } = useMemo(() => {
    let myW = 0;
    let oppW = 0;
    for (const r of rows) {
      const a = Number.parseInt(r.a, 10);
      const b = Number.parseInt(r.b, 10);
      if (Number.isNaN(a) || Number.isNaN(b)) continue;
      if (a > b) myW++;
      else if (b > a) oppW++;
    }
    const v =
      rows.length > 0 &&
      rows.every(
        (r) =>
          r.a.trim() !== "" &&
          r.b.trim() !== "" &&
          Number(r.a) !== Number(r.b), // draw-games don't exist in tt
      ) &&
      (myW >= target || oppW >= target);
    const winner =
      myW >= target
        ? active.my_team_id
        : oppW >= target
          ? active.opponent_team_id
          : null;
    return { myWins: myW, oppWins: oppW, valid: v, winnerTeamId: winner };
  }, [rows, active, target]);

  const addGame = () => setRows((r) => [...r, { a: "", b: "" }]);
  const removeGame = (i: number) => setRows((r) => r.filter((_, idx) => idx !== i));
  const update = (i: number, patch: Partial<GameRow>) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const submit = async () => {
    if (!valid || winnerTeamId == null) return;
    try {
      await report.mutateAsync({
        matchId: active.match.id,
        winner_team_id: winnerTeamId,
        games: rows.map((r) => ({ a: Number(r.a), b: Number(r.b) })),
      });
      onClose();
    } catch {
      /* error shown below */
    }
  };

  return (
    <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Badge kind="neutral">До {target} побед</Badge>
        <span style={{ marginLeft: "auto", fontSize: 13, color: "var(--fg2)" }}>
          {myWins} : {oppWins}
        </span>
      </div>

      {(() => {
        const conflict =
          active.reported_by_me &&
          active.opponent_reported &&
          active.reported_winner_team_id !== active.opponent_reported_winner_team_id;
        if (conflict) {
          return (
            <div
              style={{
                background: "rgba(223,63,64,0.1)",
                color: "var(--fg-danger)",
                padding: "8px 12px",
                borderRadius: "var(--r-md)",
                fontSize: 13,
              }}
            >
              Соперник сообщил другой счёт. Сверьте партии и исправьте — матч
              закроется, как только версии совпадут.
            </div>
          );
        }
        if (active.reported_by_me) {
          return (
            <div
              style={{
                background: "rgba(255,90,31,0.12)",
                color: "var(--fg2)",
                padding: "8px 12px",
                borderRadius: "var(--r-md)",
                fontSize: 13,
              }}
            >
              Ты уже отправил результат. Если соперник ввёл другое — можно переписать.
            </div>
          );
        }
        return null;
      })()}

      {/* Column headers so it's obvious which side is which. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12,
          color: "var(--fg3)",
          fontFamily: "var(--font-body)",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        <span style={{ width: 52 }} />
        <span style={{ width: 56, textAlign: "center" }}>Я</span>
        <span style={{ width: 10 }} />
        <span style={{ width: 56, textAlign: "center" }}>Соперник</span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map((row, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="caption" style={{ color: "var(--fg3)", width: 52 }}>
              Партия {i + 1}
            </span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={99}
              value={row.a}
              onChange={(e) => update(i, { a: e.target.value })}
              style={scoreInput}
              aria-label={`Партия ${i + 1} — мой счёт`}
              name={`score-mine-${i}`}
              {...noAutofill}
            />
            <span style={{ color: "var(--fg3)" }}>:</span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={99}
              value={row.b}
              onChange={(e) => update(i, { b: e.target.value })}
              style={scoreInput}
              aria-label={`Партия ${i + 1} — счёт соперника`}
              name={`score-opp-${i}`}
              {...noAutofill}
            />
            {rows.length > 1 && (
              <button
                type="button"
                onClick={() => removeGame(i)}
                aria-label="Убрать партию"
                style={{
                  background: "transparent",
                  border: 0,
                  color: "var(--fg3)",
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                ×
              </button>
            )}
          </div>
        ))}
        {rows.length < active.best_of && (
          <Button variant="ghost" size="sm" onClick={addGame}>
            + партия
          </Button>
        )}
      </div>

      {valid && winnerTeamId != null && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            color: "var(--brand-orange)",
            fontWeight: 600,
          }}
        >
          <Trophy size={16} />
          {winnerTeamId === active.my_team_id ? "Ты выиграл матч" : "Соперник выиграл"}
        </div>
      )}

      {report.error && (
        <div
          style={{
            background: "rgba(223,63,64,0.1)",
            color: "var(--fg-danger)",
            padding: "8px 12px",
            borderRadius: "var(--r-md)",
            fontSize: 13,
          }}
        >
          {(report.error as Error).message}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <Button fullWidth onClick={submit} disabled={!valid || report.isPending}>
          {report.isPending ? "Отправляем…" : "Подтвердить результат"}
        </Button>
        <Button fullWidth variant="ghost" onClick={onClose} disabled={report.isPending}>
          Отмена
        </Button>
      </div>
    </div>
  );
}

const scoreInput: React.CSSProperties = {
  width: 56,
  textAlign: "center",
  border: "1px solid var(--separator)",
  borderRadius: "var(--r-md)",
  padding: "8px 10px",
  background: "var(--bg)",
  color: "var(--fg1)",
  fontFamily: "var(--font-display)",
  fontWeight: 700,
  fontSize: 18,
  outline: "none",
  fontVariantNumeric: "tabular-nums",
};
